import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { showToast } from "../../../components/custom/custom-toast";
import { PAGE_SIZES, STATUS } from "../../../constants/status";
import { STRING_SUB_MENU } from "../../../constants/string";
import { useFetchData } from "../../../hooks/useFetch";
import { BrandResponse, DetailBrandResponse } from "../../../models/response/brand-response";
import { CompaniesResponse } from "../../../models/response/companies";
import { CountTabResponse } from "../../../models/response/count-tab-response";
import { API_BRANDS_ROUTER, API_COMPANIES_ROUTER } from "../../../services/api-config";
import { BasePaginateResponse } from "../../../services/base-response";
import { toggleMenuContent } from "../../../store/themeConfigSlice";
import { BrandHeader } from "../component/brand-header";
import { BrandTable } from "../component/brand-table";
import { BrandChangeStatus } from "../component/modal/brand-change-status";
import { BrandCreate } from "../component/modal/brand-create";
import { BrandDetail } from "../component/modal/brand-detail";
import { BrandInfo } from "../component/modal/brand-info";
import { BrandUpdate } from "../component/modal/brand-update";

export const BrandPage = () => {
  const dispatch = useDispatch();
  dispatch(toggleMenuContent(STRING_SUB_MENU.BRAND));
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);
  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [isOpenInfo, setIsOpenInfo] = useState(false);
  const [isOpenChangeStatus, setIsOpenChangeStatus] = useState(false);
  const [isOpenResetPass, setIsOpenResetPass] = useState(false);
  const [dataObject, setDataObject] = useState<BrandResponse>(new BrandResponse());
  const [dataDetail, setDataDetail] = useState<DetailBrandResponse>(new DetailBrandResponse());
  const [countTab, setCountTab] = useState<CountTabResponse>(new CountTabResponse());
  const [totalRecords, setTotalRecords] = useState(0);
  const [dataBrand, setDataBrand] = useState<BrandResponse[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZES[0]);
  const [keySearch, setKeySearch] = useState("");
  const [status, setStatus] = useState(STATUS.ACTIVE);
  const [dataCompanies, setDataCompanies] = useState<CompaniesResponse[]>([]);

  const { request: fetchDataCompanies, baseResponse: resCompanies } =
    useFetchData.Get<BasePaginateResponse<CompaniesResponse[]>>();
  const { request: fetchDataCountTab, baseResponse: responses } = useFetchData.Get<CountTabResponse>();
  const {
    request: fetchDataBrand,
    baseResponse: responsesData,
    loading,
  } = useFetchData.Get<BasePaginateResponse<BrandResponse[]>>();

  useEffect(() => {
    fetchDataCompanies({
      endPoint: API_COMPANIES_ROUTER.API_GET_COMPANIES,
      data: {
        status: STATUS.ACTIVE,
        key_search: "",
        page: 1,
        limit: 500,
      },
    });
  }, []);
  useEffect(() => {
    if (!resCompanies) return;
    if (resCompanies.status === 200) {
      setDataCompanies(resCompanies.data.list);
    } else {
      showToast({ message: resCompanies.message, type: "error" });
    }
  }, [resCompanies]);

  const handleCountTab = useCallback(() => {
    fetchDataCountTab({
      endPoint: API_BRANDS_ROUTER.API_GET_BRAND_COUNT_TAB(),
      data: {
        key_search: keySearch,
      },
    });
    setRefresh(false);
  }, [fetchDataCountTab, keySearch]);

  useEffect(() => {
    handleCountTab();
  }, []);

  useEffect(() => {
    if (refresh) handleCountTab();
  }, [refresh]);

  useEffect(() => {
    if (!responses) return;
    if (responses.status === 200) {
      setCountTab(responses.data);
    }
  }, [responses]);

  const handleData = () => {
    fetchDataBrand({
      endPoint: API_BRANDS_ROUTER.API_GET_BRAND(),
      data: {
        status: status,
        key_search: keySearch,
        page: page,
        limit: limit,
      },
    });
    setRefresh(false);
  };

  useEffect(() => {
    if (!responsesData) return;
    if (responsesData.status === 200) {
      setDataBrand(responsesData.data.list);
      setTotalRecords(responsesData.data.total_record);
    } else {
      showToast({ message: responsesData.message, type: "error" });
    }
  }, [responsesData]);

  useEffect(() => {
    setPage(1);
  }, [keySearch, status]);

  useEffect(() => {
    if (refresh) handleData();
  }, [refresh]);
  useEffect(() => {
    handleData();
  }, [status, keySearch, limit, page]);

  return (
    <div className="flex flex-col gap-3">
      <BrandHeader setShowModalCreate={setIsOpenCreate} countTab={countTab} setStatus={setStatus} />
      <BrandTable
        loadingTable={loading}
        dataBrand={dataBrand}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        keySearch={keySearch}
        setKeySearch={setKeySearch}
        totalRecords={totalRecords}
        setOpenModalUpdate={setIsOpenUpdate}
        setOpenModalDetail={setIsOpenDetail}
        setOpenModalChangeStatus={setIsOpenChangeStatus}
        setIsOpenResetPass={setIsOpenResetPass}
        setDataObject={setDataObject}
      />
      <BrandCreate
        isOpen={isOpenCreate}
        setIsOpen={setIsOpenCreate}
        setRefresh={setRefresh}
        setDataDetail={setDataDetail}
        setIsOpenInfo={setIsOpenInfo}
        dataCompanies={dataCompanies}
      />

      {isOpenUpdate && (
        <BrandUpdate
          isOpen={isOpenUpdate}
          setIsOpen={setIsOpenUpdate}
          brandId={dataObject._id}
          setRefresh={setRefresh}
          dataCompanies={dataCompanies}
        />
      )}

      <BrandDetail brandId={dataObject._id} isOpenDetail={isOpenDetail} setIsOpenDetail={setIsOpenDetail} />

      <BrandInfo dataDetail={dataDetail} isOpenDetail={isOpenInfo} setIsOpenDetail={setIsOpenInfo} />

      <BrandChangeStatus
        isOpen={isOpenChangeStatus}
        setIsOpen={setIsOpenChangeStatus}
        dataObject={dataObject}
        setRefresh={setRefresh}
      />
      <BrandChangeStatus
        isOpen={isOpenResetPass}
        setIsOpen={setIsOpenResetPass}
        dataObject={dataObject}
        type={STATUS.ACTIVE}
        setIsOpenInfo={setIsOpenInfo}
        setDataDetail={setDataDetail}
      />
    </div>
  );
};
